import type { Dayjs } from 'dayjs';
import type { ActivityDTO } from 'src/types/dto/activity.dto';
import { ActivityPreferredDateType } from 'src/types/dto/activity-preferred-date.dto';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Stack } from '@mui/material';

import {
  createActivityPreferredDate,
  deleteActivityPreferredDate,
  updateActivityPreferredDate,
} from 'src/api/activity-preferred-dates';
import { RangeAvailabilityCalendar } from 'src/components/range-availability-calendar';
import { toast } from 'src/components/snackbar';

import { ActivityDatePreferences } from './activity-date-preferences';
import { ActivityAddDatePreferenceDialog } from './activity-add-date-preference-dialog';
import {
  deriveActivityDatePreferences,
  formatActivityPreferredDate,
} from './derive-activity-date-preferences';
import { preferencesToCalendarRanges } from './preferences-to-calendar-ranges';
import type { ActivityDatePreference } from './activity-date-preference.types';
import { isPersistedActivityDatePreference } from './activity-date-preference.types';

interface ActivityDatesTabProps {
  activity: ActivityDTO;
  onActivityUpdate?: () => void | Promise<void>;
}

export const ActivityDatesTab = memo(({ activity, onActivityUpdate }: ActivityDatesTabProps) => {
  const { t } = useTranslation('activities');
  const [preferences, setPreferences] = useState<ActivityDatePreference[]>(() =>
    deriveActivityDatePreferences(activity)
  );
  const [isPreferenceDialogOpen, setIsPreferenceDialogOpen] = useState(false);
  const [editingPreference, setEditingPreference] = useState<ActivityDatePreference | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setPreferences(deriveActivityDatePreferences(activity));
    setEditingPreference(null);
    setIsPreferenceDialogOpen(false);
  }, [activity]);

  const calendarRanges = useMemo(
    () => preferencesToCalendarRanges(preferences),
    [preferences]
  );

  const legendLabels = useMemo(
    () => ({
      window: t('activityModal.dates.calendar.window'),
      preferred: t('activityModal.dates.calendar.preferred'),
      excluded: t('activityModal.dates.calendar.excluded'),
    }),
    [t]
  );

  const refreshActivity = useCallback(async () => {
    await onActivityUpdate?.();
  }, [onActivityUpdate]);

  const handleAddPreference = useCallback(() => {
    setEditingPreference(null);
    setIsPreferenceDialogOpen(true);
  }, []);

  const handleEditPreference = useCallback((preference: ActivityDatePreference) => {
    setEditingPreference(preference);
    setIsPreferenceDialogOpen(true);
  }, []);

  const handleDeletePreference = useCallback(
    async (preferenceId: string | number) => {
      const preference = preferences.find((item) => item.id === preferenceId);
      if (!preference || !isPersistedActivityDatePreference(preference)) {
        return;
      }

      setIsSaving(true);
      try {
        await deleteActivityPreferredDate(activity.id, preference.id);
        await refreshActivity();
        toast.success(t('activityModal.dates.toast.deleteSuccess'));
      } catch {
        toast.error(t('activityModal.dates.toast.deleteError'));
      } finally {
        setIsSaving(false);
      }
    },
    [activity.id, preferences, refreshActivity, t]
  );

  const handleClosePreferenceDialog = useCallback(() => {
    if (isSaving) {
      return;
    }
    setIsPreferenceDialogOpen(false);
    setEditingPreference(null);
  }, [isSaving]);

  const handleApplyPreference = useCallback(
    async (payload: { type: 'preferred' | 'excluded'; start: Dayjs; end: Dayjs }) => {
      const body = {
        type:
          payload.type === 'preferred'
            ? ActivityPreferredDateType.PREFERRED
            : ActivityPreferredDateType.EXCLUDED,
        startDate: formatActivityPreferredDate(payload.start),
        endDate: formatActivityPreferredDate(payload.end),
      };

      setIsSaving(true);
      try {
        if (editingPreference && isPersistedActivityDatePreference(editingPreference)) {
          await updateActivityPreferredDate(activity.id, editingPreference.id, body);
        } else {
          await createActivityPreferredDate(activity.id, body);
        }

        await refreshActivity();
        toast.success(t('activityModal.dates.toast.saveSuccess'));
        setIsPreferenceDialogOpen(false);
        setEditingPreference(null);
      } catch {
        toast.error(t('activityModal.dates.toast.saveError'));
      } finally {
        setIsSaving(false);
      }
    },
    [activity.id, editingPreference, refreshActivity, t]
  );

  return (
    <Stack spacing={2} sx={{ p: 2, minWidth: 0 }}>
      <Box
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          p: 2,
        }}
      >
        <RangeAvailabilityCalendar
          window={calendarRanges.window}
          preferred={calendarRanges.preferred}
          excluded={calendarRanges.excluded}
          defaultVisibleMonth={calendarRanges.window.from.startOf('month')}
          monthsToShow={4}
          legendLabels={legendLabels}
          readOnly
        />
      </Box>

      <ActivityDatePreferences
        preferences={preferences}
        isSaving={isSaving}
        onAddPreference={handleAddPreference}
        onEditPreference={handleEditPreference}
        onDeletePreference={handleDeletePreference}
      />

      <ActivityAddDatePreferenceDialog
        open={isPreferenceDialogOpen}
        completionWindow={calendarRanges.window}
        editingPreference={editingPreference}
        isSaving={isSaving}
        onClose={handleClosePreferenceDialog}
        onApply={handleApplyPreference}
      />
    </Stack>
  );
});

ActivityDatesTab.displayName = 'ActivityDatesTab';
